import { Controller } from "@hotwired/stimulus"
import { anchorMenu } from "../lib/hudini/floating_menu.js"

// The composer's `/` command menu, opened via the Skills button or a `/query`
// typed in the composer. Rows are grouped into sections and carry a
// `data-kind`: prompt templates insert their text, media rows open the file
// picker, and skills toggle an inline, non-editable token inside the
// contenteditable composer (Linear-style) that can be deleted like a single
// character. Skill selection state therefore lives in the DOM (the tokens)
// rather than a separate Set; the `hudini` controller reads the ids on send.
//
// The menu is positioned with floating-ui anchored to the composer, so it
// opens above or below based on available space and stays put while typing.
export default class extends Controller {
  static targets = [
    "prompt", "skillMenu", "skillOption", "skillEmpty", "skillTrigger", "skillPicker",
    "section",
  ]

  connect() {
    this.skillMenuOpen = false
    this.slashInfo = null
    this.cleanupFloating = null
    this.boundCloseSkillMenu = this.closeSkillMenuOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseSkillMenu)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseSkillMenu)
    this.stopFloating()
  }

  // --- Selection state (derived from the inline tokens) ----------------------

  get editor() {
    return this.hasPromptTarget ? this.promptTarget : null
  }

  tokenElements() {
    return this.editor ? Array.from(this.editor.querySelectorAll("[data-skill-id]")) : []
  }

  // Deduped, in-document order of the selected skill ids.
  get selectedIdsArray() {
    const seen = new Set()
    const ids = []
    this.tokenElements().forEach(token => {
      const id = token.dataset.skillId
      if (id && !seen.has(id)) {
        seen.add(id)
        ids.push(id)
      }
    })
    return ids
  }

  hasSkill(id) {
    return this.tokenElements().some(token => token.dataset.skillId === String(id))
  }

  // --- Keyboard navigation while the menu is open ----------------------------

  // Stops propagation so the composer's own Enter/Escape handling (send / stop)
  // doesn't also fire.
  handleKeydown(event) {
    if (!this.skillMenuOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault(); event.stopImmediatePropagation(); this.moveActiveSkill(1); break
      case "ArrowUp":
        event.preventDefault(); event.stopImmediatePropagation(); this.moveActiveSkill(-1); break
      case "Enter":
      case "Tab":
        event.preventDefault(); event.stopImmediatePropagation(); this.selectActiveSkill(); break
      case "Escape":
        event.preventDefault(); event.stopImmediatePropagation(); this.closeSkillMenu(); break
    }
  }

  // --- Slash command ---------------------------------------------------------

  // Opens the menu when the token before the caret is a `/command`, e.g. at the
  // start of the message or after whitespace. The captured text filters skills.
  detectSlash() {
    if (!this.hasSkillMenuTarget) return

    const info = this.caretSlashInfo()
    if (info) {
      this.slashInfo = info
      this.openSkillMenu(info.query)
    } else if (this.skillMenuOpen && this.slashInfo) {
      this.closeSkillMenu()
    }
  }

  // Inspects the collapsed caret and returns the active `/query` (with the text
  // node and the index of the `/`) so it can be replaced by a token on select.
  caretSlashInfo() {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !this.editor) return null

    const range = sel.getRangeAt(0)
    if (!range.collapsed) return null

    const node = range.endContainer
    if (node.nodeType !== Node.TEXT_NODE || !this.editor.contains(node)) return null

    const before = node.textContent.slice(0, range.endOffset)
    const match = before.match(/(?:^|\s)\/([^\s/]*)$/)
    if (!match) return null

    return { node, start: range.endOffset - match[1].length - 1, query: match[1] }
  }

  // --- Menu open/close/filter ------------------------------------------------

  toggleSkillMenu(event) {
    event.preventDefault()
    if (this.skillMenuOpen) {
      this.closeSkillMenu()
    } else {
      this.slashInfo = null
      this.openSkillMenu("")
      if (this.editor) this.editor.focus()
    }
  }

  openSkillMenu(query) {
    if (!this.hasSkillMenuTarget) return

    // Only one composer menu at a time — the model picker's stacking context
    // would otherwise sit on top of this menu and eat option clicks.
    this.closeModelMenu()

    this.skillMenuOpen = true
    this.skillMenuTarget.classList.remove("hidden")
    if (this.hasSkillTriggerTarget) this.skillTriggerTarget.classList.add("hudini__skill-trigger--open")
    this.pruneDeletedSkillTokens()
    this.syncSkillOptions()
    this.filterSkills(query)
    this.startFloating()
  }

  startFloating() {
    this.stopFloating()
    if (!this.editor || !this.hasSkillMenuTarget) return

    this.cleanupFloating = anchorMenu(this.editor, this.skillMenuTarget)
  }

  stopFloating() {
    if (this.cleanupFloating) {
      this.cleanupFloating()
      this.cleanupFloating = null
    }
  }

  closeModelMenu() {
    const model = this.application.getControllerForElementAndIdentifier(this.element, "hudini-model")
    if (model) model.closeMenu()
  }

  closeSkillMenu() {
    if (!this.hasSkillMenuTarget) return

    this.stopFloating()
    this.skillMenuOpen = false
    this.slashInfo = null
    this.skillMenuTarget.classList.add("hidden")
    if (this.hasSkillTriggerTarget) this.skillTriggerTarget.classList.remove("hudini__skill-trigger--open")
  }

  closeSkillMenuOnOutsideClick(event) {
    if (!this.skillMenuOpen) return
    if (this.hasSkillPickerTarget && this.skillPickerTarget.contains(event.target)) return
    if (this.editor && this.editor.contains(event.target)) return
    this.closeSkillMenu()
  }

  // Used by the "New Skill" and edit links: close the picker but let the click
  // keep bubbling so Turbo can intercept it and open the modal.
  closeSkillMenuForNav() {
    this.closeSkillMenu()
  }

  filterSkills(query) {
    const q = query.trim().toLowerCase()
    let visible = 0

    this.skillOptionTargets.forEach(option => {
      const match = option.dataset.name.toLowerCase().includes(q)
      option.classList.toggle("hidden", !match)
      option.classList.remove("hudini__skill-option--active")
      if (match) visible++
    })

    // Hide a section (heading included) when none of its rows match. An
    // unfiltered menu keeps the skills section's "No skills yet" empty state
    // visible even though it has no rows.
    this.sectionTargets.forEach(section => {
      const hasVisible = Array.from(section.querySelectorAll(".hudini__skill-option"))
        .some(option => !option.classList.contains("hidden"))
      const showsEmptyState = q === "" && section.querySelector(".hudini__skill-empty-state")
      section.classList.toggle("hidden", !(hasVisible || showsEmptyState))
    })

    this.skillEmptyTarget.classList.toggle("hidden", visible > 0)
    this.setActiveSkill(0)
  }

  get visibleSkillOptions() {
    return this.skillOptionTargets.filter(option => !option.classList.contains("hidden"))
  }

  setActiveSkill(index) {
    const options = this.visibleSkillOptions
    if (options.length === 0) return

    this.activeSkillIndex = Math.max(0, Math.min(index, options.length - 1))
    options.forEach((option, i) => {
      option.classList.toggle("hudini__skill-option--active", i === this.activeSkillIndex)
    })
    options[this.activeSkillIndex].scrollIntoView({ block: "nearest" })
  }

  moveActiveSkill(delta) {
    this.setActiveSkill((this.activeSkillIndex || 0) + delta)
  }

  hoverSkill(event) {
    const index = this.visibleSkillOptions.indexOf(event.currentTarget)
    if (index >= 0) this.setActiveSkill(index)
  }

  // --- Selecting options ------------------------------------------------------

  selectActiveSkill() {
    const option = this.visibleSkillOptions[this.activeSkillIndex || 0]
    if (option) this.activateOption(option)
  }

  // Use mousedown (not click): the menu's keepFocus calls preventDefault on
  // mousedown so the contenteditable doesn't blur, and that can swallow the
  // subsequent click.
  selectOption(event) {
    // Edit links should open the modal on click, not activate the row here.
    if (event.target && event.target.closest("a")) return

    event.preventDefault()
    this.activateOption(event.currentTarget)
  }

  // Rows carry a `data-kind`: prompt templates insert their text, media rows
  // open the file picker, and skills toggle an inline token.
  activateOption(option) {
    switch (option.dataset.kind) {
      case "prompt":
        this.insertPrompt(option.dataset.prompt)
        break
      case "media":
        this.attachMedia()
        break
      default:
        this.toggleSkill(option.dataset.id, option.dataset.name)
    }
  }

  // Inserts the prompt template at the caret (replacing the typed `/query`).
  // The templates end mid-sentence; the trailing space becomes an nbsp so the
  // caret visibly sits after it and the user can keep typing.
  insertPrompt(prompt) {
    const range = this.replaceSlashRange()
    if (!range) return

    const text = document.createTextNode((prompt || "").replace(/ $/, "\u00A0"))
    range.insertNode(text)
    range.setStartAfter(text)
    range.collapse(true)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)

    this.closeSkillMenu()
    this.notifyInput()
  }

  // Removes the typed `/query`, then opens the same file picker as the
  // paperclip button (owned by the sibling hudini-context controller).
  attachMedia() {
    const range = this.replaceSlashRange()
    if (range) {
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    }

    this.closeSkillMenu()
    this.notifyInput()

    const context = this.application.getControllerForElementAndIdentifier(this.element, "hudini-context")
    if (context && context.hasFileInputTarget) context.fileInputTarget.click()
  }

  // Adds or removes a skill, then closes the menu so a pick feels like a
  // single choice (re-open to add another).
  toggleSkill(id, name) {
    if (this.hasSkill(id)) {
      this.removeSkillToken(id)
    } else {
      this.insertSkillToken(id, name)
    }

    this.syncSkillOptions()
    this.closeSkillMenu()
    if (this.editor) this.editor.focus()
  }

  // Inserts an inline, non-editable token at the caret (replacing the typed
  // `/query` when present, otherwise at the current caret / end of the editor).
  insertSkillToken(id, name) {
    const range = this.replaceSlashRange()
    if (!range) return

    const token = this.buildToken(id, name)
    range.insertNode(token)

    // Trailing space so the caret can sit after the token and the user can keep
    // typing immediately.
    const space = document.createTextNode("\u00A0")
    range.setStartAfter(token)
    range.collapse(true)
    range.insertNode(space)
    range.setStartAfter(space)
    range.collapse(true)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)

    this.notifyInput()
  }

  // Returns a caret range with the typed `/query` removed (or at the current
  // caret / end of the editor when the menu was opened from the button).
  replaceSlashRange() {
    const editor = this.editor
    if (!editor) return null

    editor.focus()
    const sel = window.getSelection()
    let range

    if (this.slashInfo) {
      const { node, start } = this.slashInfo
      const caret = (sel.rangeCount && sel.getRangeAt(0).endContainer === node)
        ? sel.getRangeAt(0).endOffset
        : node.textContent.length
      node.textContent = node.textContent.slice(0, start) + node.textContent.slice(caret)
      range = document.createRange()
      range.setStart(node, start)
      range.collapse(true)
    } else if (sel.rangeCount && editor.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0)
      range.deleteContents()
    } else {
      range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
    }

    this.slashInfo = null
    return range
  }

  removeSkillToken(id) {
    this.tokenElements()
      .filter(token => token.dataset.skillId === String(id))
      .forEach(token => token.remove())
    this.notifyInput()
  }

  // Matches InlineMentionComponent (variant: :skill) so composer tokens share
  // the design-system mention markup and styles.
  buildToken(id, name) {
    const token = document.createElement("span")
    token.className = "inline-mention inline-mention--skill"
    token.contentEditable = "false"
    token.dataset.skillId = id
    token.dataset.skillName = name

    const iconWrap = document.createElement("span")
    iconWrap.className = "inline-mention__icon"
    const icon = document.createElement("i")
    icon.className = "fas fa-pen-swirl"
    iconWrap.appendChild(icon)

    const text = document.createElement("span")
    text.className = "inline-mention__text"
    text.textContent = name

    token.appendChild(iconWrap)
    token.appendChild(text)
    return token
  }

  // Rows representing skills (prompt/media rows carry no skill id).
  get skillRows() {
    return this.skillOptionTargets.filter(option => option.dataset.id)
  }

  // Keeps the menu checkmarks in sync with the tokens currently in the editor.
  syncSkillOptions() {
    this.skillRows.forEach(option => {
      option.classList.toggle("hudini__skill-option--selected", this.hasSkill(option.dataset.id))
    })
  }

  // Drops tokens whose skill no longer exists (e.g. deleted in the modal) so a
  // stale token never gets sent.
  pruneDeletedSkillTokens() {
    const ids = new Set(this.skillRows.map(option => option.dataset.id))
    this.tokenElements().forEach(token => {
      if (!ids.has(token.dataset.skillId)) token.remove()
    })
  }

  // Keeps freshly rendered options (after a create/edit/delete) showing the
  // right checkmark for the current selection.
  skillOptionTargetConnected(element) {
    if (element.dataset.id) {
      element.classList.toggle("hudini__skill-option--selected", this.hasSkill(element.dataset.id))
    }
  }

  // Prevent the contenteditable from stealing focus when clicking menu rows,
  // but let the search input (and other fields) receive focus normally.
  keepFocus(event) {
    if (event.target.closest("input, textarea, select, [contenteditable]")) return
    event.preventDefault()
  }

  // Notifies the host so it can refresh its empty/placeholder state after tokens
  // change programmatically.
  notifyInput() {
    if (this.editor) this.editor.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
